import { useEditor } from 'src/contexts/EditorContext'
import { useTranscriptUserStateManager } from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useConfig } from 'src/contexts/ConfigurationContext'
import { HOTKEYS } from 'src/utils/hotkeys'
import { paragraphPointAt } from 'src/utils/highlightRange'

import { contentEntryPresentation, ContentEntry, ParagraphTextProps, ParagraphTextWrapper, useGreyOutUnfinalizedAsr } from './shared'

export const ParagraphTextReadonly = (props: ParagraphTextProps) => {
    const { textElementRef, paragraph, color, backgroundColor } = props
    const { isTranscriptEditAuthorised } = useEditor()
    const userStateManager = useTranscriptUserStateManager()
    const viewMode = useTranscriptMetadata()?.viewMode
    const [config] = useConfig()
    const greyOutUnfinalizedAsr = useGreyOutUnfinalizedAsr()

    // Change from onClick to onMouseDown to prevent the QuickActionMenuBar from being closed
    // when the user clicks on the paragraph
    const paragraphContainsEditableText = paragraph.content.some((contentEntry) => contentEntry.isEditable)
    const canParagraphBeEdited = isTranscriptEditAuthorised && paragraphContainsEditableText
    const onParagraphMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (canParagraphBeEdited) {
            e.preventDefault()
            const offset = paragraphPointAt(e.clientX, e.clientY)?.offset ?? 0
            userStateManager.updateSelection({ paragraphId: paragraph.id, offset })
        }
    }

    const onParagraphKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (canParagraphBeEdited && (HOTKEYS.enter.check(event) || HOTKEYS.space.check(event))) {
            event.preventDefault()
            userStateManager.updateSelection({ paragraphId: paragraph.id, offset: 0 })
        }
    }

    return (
        <ParagraphTextWrapper
            ref={textElementRef}
            data-paragraph-id={paragraph.id}
            $color={color}
            $backgroundColor={backgroundColor}
            $textTransform={config.shouldShowTranscriptInAllCaps ? 'uppercase' : undefined}
            tabIndex={canParagraphBeEdited ? 0 : undefined}
            onMouseDown={onParagraphMouseDown}
            onKeyDown={onParagraphKeyDown}>
            {paragraph.content.map((contentEntry, contentIndex) => {
                const { isGreyedOut, isSelectable } = contentEntryPresentation({
                    viewMode,
                    isTranscriptEditAuthorised,
                    isEditable: contentEntry.isEditable,
                    greyOutUnfinalizedAsr,
                })

                return (
                    <ContentEntry key={`${contentIndex}-${contentEntry.text}`} $isGreyedOut={isGreyedOut} $isSelectable={isSelectable}>
                        {contentEntry.text}
                    </ContentEntry>
                )
            })}
        </ParagraphTextWrapper>
    )
}
